/**
 * captureRouting — central classification + routing pipeline for the
 * UnifiedCaptureSurface (Phase G.1, 2026-05-01).
 *
 * Bill's vision: officer drops/scans/captures ANY document into ONE central
 * surface, the system classifies the document type, routes to the correct
 * slot, runs extraction, and auto-fills the matching person fields. This
 * module centralizes the doc-type → (slot key, extraction model, mapper)
 * routing table so the surface itself stays presentational.
 *
 * G.1 scope (locked):
 *   - 15 doc types covering every classifier branch the dialog cares about
 *   - 3 extraction backends: prebuilt-idDocument (DL/State ID/US Passport),
 *     /v3/nra/scan (foreign passport / visa / I-94 / I-20 / DS-2019 /
 *     green card / EAD / SSN), and no-extraction (birth cert, address proof,
 *     ITIN letter, "Other")
 *   - Empty-fields-only patch: only set RaoPerson fields that are CURRENTLY
 *     empty so we never clobber officer-typed values
 *   - Slot priority is FIFO into the matching slot — later capture replaces
 *     earlier in the same slot (existing AttachedDoc semantics in the
 *     dialog's registerAttachedDoc)
 *
 * Deferred to later phases:
 *   - G.2 — slot priority + auto-promote/demote (e.g. "primary ID" rotates
 *     between US passport and DL when both are captured)
 *   - G.3 — cross-doc validation + conflict UI when DL says one DOB and
 *     passport says another
 *   - G.4 — auto-classification beyond Azure DI's documentType field
 *     (currently we trust Azure DI for DL/Passport/State ID and otherwise
 *     default to "Other / Unrecognized" — officer overrides via dropdown)
 *   - G.5 — deprecate the per-tab capture surfaces in favor of this one
 */

import type { ApiClient } from '@retail-ops/api-client';
import type {
  NraDocumentType,
  UploadAttachedRequest,
  UploadAttachedSourceType,
} from '@retail-ops/api-client';
import type { RaoPerson } from '../../../types.js';
import type { AttachedDoc } from '../shared/attachedDocs.js';
import { estimateBase64Size } from '../shared/attachedDocs.js';
import { IdExtractionService, type ExtractedIdFields } from '../../../services/IdExtractionService.js';
import { sha256OfBase64 } from '../../../utils/fileHash.js';
import { normalizeIso2 } from '../../../utils/isoCountry.js';
import { pdfFirstPageThumbnailFromBase64 } from '../../../utils/pdfThumbnail.js';
import { parseForeignName } from '../../../utils/parseForeignName.js';
import { normalizeVisaType } from '../../../utils/normalizeVisaType.js';
import { serializeExtract, type RetainedExtractField } from '../shared/nraScanPatch.js';
import type { CaptureConflict } from '../capture/extractDisplay.js';
import { resolveCountryIso2, resolveCountryName } from './useCountryCatalog.js';
import { toNamedJsonDate } from '../../../../../utils/jxDates.js';
import { joinStreetLines } from '../shared/streetLine';

/**
 * Normalize an extracted date to ISO "YYYY-MM-DD" before it lands on the
 * person. Azure DI returns document dates in US "MM/DD/YYYY" — persisting
 * that raw broke BOTH the <input type="date"> hydration (renders blank) and
 * the commit pipeline (Core Director rejects a non-ISO BirthDt with System
 * Error 1 — Sample B walk, 2026-07-02). Unparseable values pass through
 * unchanged so nothing is silently lost; validation surfaces them.
 */
function isoDate(v: string): string {
  return toNamedJsonDate(v) || v;
}

/**
 * Citizenship value → ISO alpha-2, tolerant of all three shapes the analyzers
 * emit: alpha-2 ("IN"), alpha-3 ("IND" — passport MRZ), and full country
 * names ("INDIA" — the I-20 form prints the name, NRA smoke 2026-06-12).
 * Falls through to the uppercased input when nothing matches (no value loss).
 *
 * Bill ruling #3 (2026-06-12) — resolution now runs through the SQL-backed
 * CountryCatalog (useCountryCatalog module cache when /v3/config/countries
 * has loaded; the legacy static list otherwise). ONE util for every country
 * shape — see resolveCountryEntry in useCountryCatalog.ts.
 */
function citizenshipToIso2(raw: string): string {
  return resolveCountryIso2(raw);
}

// -----------------------------------------------------------------------------
// Doc-type catalog
// -----------------------------------------------------------------------------

/**
 * Routing strategy for each doc type:
 *   - 'azure-di-id'  → /accounts/scan-id-document (prebuilt-idDocument)
 *                      Used for US/CA DL, State ID, US Passport. Returns the
 *                      full demographic + CIP-field bundle.
 *   - 'v3-nra'       → /v3/nra/scan with documentType=<…>
 *                      Used for foreign passport / visa / I-94 / I-20 /
 *                      DS-2019 / green card / EAD / SSN. Each one has its
 *                      own field family that maps to person.<canonical> +
 *                      person.extra.
 *   - 'no-extraction'→ Just attach the doc; no field-extraction pass.
 *                      Used for birth cert, address proof, ITIN letter, Other.
 */
export type CaptureExtractionMode = 'azure-di-id' | 'v3-nra' | 'no-extraction';

export interface DocTypeOption {
  /** Stable key for dropdown value + history. Doc-type — NOT the slot. */
  value: string;
  /** Officer-facing label. */
  label: string;
  /**
   * AttachedDoc.docCode — the slot this capture lands in. Multiple doc
   * types can map to the same slot (e.g. DL and State ID both fill DL_FRONT).
   * Existing AttachedDoc storage replaces by docCode (FIFO).
   */
  slot: string;
  /** Extraction backend selector. */
  mode: CaptureExtractionMode;
  /** When mode === 'v3-nra', which documentType to send to the endpoint. */
  nraType?: NraDocumentType;
  /** Short description used in result panel + tooltip. */
  hint?: string;
}

export const DOC_TYPE_OPTIONS: ReadonlyArray<DocTypeOption> = [
  // --- Photo IDs handled by Azure DI prebuilt-idDocument ---
  { value: 'us-drivers-license', label: "Driver's License (US/CA)", slot: 'DL_FRONT',
    mode: 'azure-di-id', hint: 'US or Canadian driver license, front side.' },
  { value: 'state-id',           label: 'State ID',                  slot: 'DL_FRONT',
    mode: 'azure-di-id', hint: 'Non-driver state-issued photo ID.' },
  { value: 'us-passport',        label: 'US Passport',               slot: 'PASSPORT',
    mode: 'azure-di-id', hint: 'Data page of a US-issued passport book or card.' },

  // --- NRA/foreign docs handled by /v3/nra/scan ---
  { value: 'foreign-passport',   label: 'Foreign Passport',          slot: 'PASSPORT',
    mode: 'v3-nra', nraType: 'passport', hint: 'Non-US passport — extracts country + foreign passport number.' },
  { value: 'us-visa',            label: 'US Visa',                   slot: 'VISA',
    mode: 'v3-nra', nraType: 'visa', hint: 'US visa foil page (typically pasted into the passport).' },
  { value: 'i94',                label: 'I-94',                      slot: 'I94',
    mode: 'v3-nra', nraType: 'i94', hint: 'CBP I-94 admission record (printout from i94.cbp.dhs.gov).' },
  { value: 'i20',                label: 'I-20 (F-1 student)',        slot: 'I20',
    mode: 'v3-nra', nraType: 'i20', hint: 'DHS/ICE I-20 (F-1/F-2/M-1 students).' },
  { value: 'ds2019',             label: 'DS-2019 (J-1)',             slot: 'DS2019',
    mode: 'v3-nra', nraType: 'ds2019', hint: 'DOS DS-2019 (J-1/J-2 exchange visitors).' },
  { value: 'green-card',         label: 'Permanent Resident Card',   slot: 'GREEN_CARD',
    mode: 'v3-nra', nraType: 'green-card', hint: 'I-551 Permanent Resident (green) card.' },
  { value: 'ead',                label: 'EAD (I-766)',               slot: 'EAD',
    mode: 'v3-nra', nraType: 'ead', hint: 'I-766 Employment Authorization Document.' },
  { value: 'primary-id',         label: 'Primary Photo ID',          slot: 'PRIMARY_ID',
    mode: 'azure-di-id', hint: 'US-Citizen / Permanent-Resident primary photo ID (DL / State ID / US passport). prebuilt-idDocument self-classifies via IdExtractionService — proven SPFx pipeline (splitFirstMiddle · parseAddress · postProcessAddress · USPS · titleCase · aiOriginals).' },
  // Step 2 (Bill 2026-05-28) — generic Secondary Photo ID slot. Identical
  // routing to primary-id (prebuilt-idDocument self-classify); separate slot
  // so the CIP slot-machine can land Primary + Secondary in the right tiles.
  { value: 'secondary-id',       label: 'Secondary Photo ID',        slot: 'SECONDARY_ID',
    mode: 'azure-di-id', hint: 'Additional photo ID (any subtype). Auto-classifies + ranks vs primary by F254 IdVerifyCode priority.' },
  { value: 'ssn-card',           label: 'SSN Card',                  slot: 'SSN_CARD',
    mode: 'v3-nra', nraType: 'ssn', hint: 'Social Security Administration card.' },

  // --- No-extraction (just attach) ---
  { value: 'birth-cert',         label: 'Birth Certificate',         slot: 'BIRTH_CERT',
    mode: 'no-extraction', hint: 'State-issued birth certificate (minors / no-photo-ID).' },
  { value: 'address-proof',      label: 'Address Proof',             slot: 'ADDR_PROOF',
    mode: 'v3-nra', nraType: 'address-proof', hint: 'Utility bill / lease / official mail — auto-fills Street/City/State/Zip on the Contact tab.' },
  { value: 'itin-letter',        label: 'ITIN Letter',               slot: 'ITIN_LTR',
    mode: 'no-extraction', hint: 'IRS ITIN assignment notice (CP565).' },
  // BACKLOG-0600 — manual NRA work-cohort docs (slot-1 T4). These existed as
  // Stage-4 cohort tiles (cohortDocTiles nra-work) WITHOUT DOC_TYPE_OPTIONS
  // rows, so any non-tile capture path resolved them to 'other'. No trained
  // extractor — attach-only. Source: USCIS Form I-797 approval notice +
  // visa-class proof bucket (intracompany letter / O-1 petition / TN offer);
  // canonical Stage-9 DocCodes I797 / VISA_CLASS_PROOF (tileIdToCanonicalDocCode).
  // 2026-06-12 — label aligned with cohortDocTiles' "I-797 approval notice"
  // relabel (petition-based classes H/L/O; no longer H-1B-specific).
  { value: 'i797',               label: 'I-797 approval notice',     slot: 'I797',
    mode: 'no-extraction', hint: 'USCIS approval notice (petition-based classes H/L/O). Manual attach — no auto-extraction.' },
  { value: 'visa-class-proof',   label: 'Visa-class proof',          slot: 'VISA_CLASS_PROOF',
    mode: 'no-extraction', hint: 'Intracompany letter (L-1) / O-1 petition / TN offer. Manual attach — no auto-extraction.' },
  { value: 'other',              label: 'Other / Unrecognized',      slot: 'OTHER',
    mode: 'no-extraction', hint: 'Catch-all bucket; system will attach without extraction.' },
];

/**
 * BACKLOG-0600 (punch-list S5.1) — canonical doc-type vocabulary.
 *
 * THE canonical doc-type identifier set is `DOC_TYPE_OPTIONS[].value` above.
 * Everything else — cohort tile ids, `/v3/nra/scan` wire ids (`nraType`),
 * NRA-classifier labels — is an ALIAS that must normalize into this set at the
 * routing boundary. The alias map covers every legacy id either capture path
 * has produced, so in-flight carts / persons (extracts + drafts keyed by old
 * ids) keep resolving. The BE mirror lives in
 * `NraDocumentScanService.NormalizeDocType` — keep the two in lockstep.
 *
 * 18 USC § 701 — US Military ID / CAC is deliberately ABSENT from this
 * vocabulary and MUST NEVER be added as a capturable/classifiable type
 * (federal ban on copying; FSR records the fields manually, no image
 * retained). Core Director F254 code 4 exists for manual key-in only.
 */
const LEGACY_DOC_TYPE_ALIASES: Readonly<Record<string, string>> = {
  // /v3/nra/scan wire ids (nraType) — also the pre-2026-06 Stage-4 / NRA-
  // section tile ids (cohortDocTiles used 'passport' / 'visa' raw).
  'passport':  'foreign-passport',
  'visa':      'us-visa',
  'ssn':       'ssn-card',
  // NRA classifier label variant (see nraClassToDocTypeValue).
  'greencard': 'green-card',
};

/**
 * Resolve ANY doc-type identifier (canonical value, wire id, legacy tile id)
 * to the canonical DOC_TYPE_OPTIONS value. Unknown ids return as-is so the
 * caller's existing 'other' fallback still applies.
 */
export function normalizeDocTypeValue(raw: string): string {
  return LEGACY_DOC_TYPE_ALIASES[raw] ?? raw;
}

export function findDocType(value: string): DocTypeOption | undefined {
  // 0600 — tolerant lookup: exact canonical match first, then the legacy-
  // alias normalization ('passport' → 'foreign-passport', 'ssn' → 'ssn-card').
  return DOC_TYPE_OPTIONS.find((o) => o.value === value)
    ?? DOC_TYPE_OPTIONS.find((o) => o.value === normalizeDocTypeValue(value));
}

/**
 * Step 2 / Step 5 — tile-branding metadata per doc type. The PDW Stage-4
 * generic "Primary Photo ID" / "Secondary Photo ID" tiles ADOPT the
 * classified doc's label + icon + Core Director F254 IdVerifyCode after
 * capture. The F254 value also drives the CIP slot-machine priority
 * (ascending F254 → Primary slot first, Secondary slot second — Bill
 * 2026-05-27 reference_cd_idverifycode_field254_verified_20260527).
 *
 * F254 IdVerifyCode (probe-verified 2026-05-27, JHA Core Director live):
 *   1 Driver's License        4 Military ID       7 Birth Certificate
 *   2 State ID                5 SSN Card          8 Other
 *   3 Passport                6 State Documents   9 Lexis Nexis (system)
 *   (0 Blank, 9 system-only — both excluded from FSR-driven ranking)
 *
 * Lower F254 = higher priority. A DL captured second after a Passport
 * BUMPS the Passport from Primary to Secondary (DL=1 outranks Passport=3).
 *
 * Brand icons reuse the existing DOCUMENT_ICONS slug set in
 * apps/ROP-teams/src/icons/flat-library.ts — no new icon assets.
 */
export interface DocTypeBranding {
  /** Officer-facing label rendered on the captured tile + drawer header. */
  label: string;
  /** DOCUMENT_ICONS slug used by documentIconUrl (existing icon set). */
  iconSlug: string;
  /**
   * Core Director F254 IdVerifyCode (string '1'..'8'). Drives CIP slot-
   * machine priority + persists on person.extra.idType. Undefined for
   * doc types that don't slot into the F254 CIP-ID space (visa, i94,
   * i20, ds2019, ead, address-proof — these are supporting docs, not
   * primary identity documents).
   */
  idCodeF254?: string;
}

const DOC_TYPE_BRANDING: Readonly<Record<string, DocTypeBranding>> = {
  'us-drivers-license': { label: "Driver's License",       iconSlug: 'drivers-license',   idCodeF254: '1' },
  'state-id':           { label: 'State ID',               iconSlug: 'drivers-license',   idCodeF254: '2' },
  'us-passport':        { label: 'US Passport',            iconSlug: 'us-passport',       idCodeF254: '3' },
  'foreign-passport':   { label: 'Foreign Passport',       iconSlug: 'foreign-passport',  idCodeF254: '3' },
  'us-visa':            { label: 'US Visa',                iconSlug: 'visa' },
  'i94':                { label: 'I-94',                   iconSlug: 'i94' },
  'i20':                { label: 'I-20',                   iconSlug: 'i20' },
  'ds2019':             { label: 'DS-2019',                iconSlug: 'ds2019' },
  'green-card':         { label: 'Permanent Resident Card', iconSlug: 'green-card',       idCodeF254: '3' },
  'ead':                { label: 'EAD (I-766)',            iconSlug: 'ead' },
  'ssn-card':           { label: 'SSN Card',               iconSlug: 'ssn-card',          idCodeF254: '5' },
  'birth-cert':         { label: 'Birth Certificate',      iconSlug: 'birth-certificate', idCodeF254: '7' },
  'address-proof':      { label: 'Address Verification',   iconSlug: 'pcb-address-verification' },
  'itin-letter':        { label: 'ITIN Letter',            iconSlug: 'tax-form' },
  'other':              { label: 'Other Document',         iconSlug: 'tax-form',          idCodeF254: '8' },
  // primary-id is the GENERIC slot itself — re-resolves to the detected
  // doc type's branding via docTypeBranding(detectedValue).
  'primary-id':         { label: 'Primary Photo ID',       iconSlug: 'drivers-license' },
};

export function docTypeBranding(value: string): DocTypeBranding | undefined {
  // 0600 — tolerant lookup mirrors findDocType ('passport' tile id resolves
  // the foreign-passport branding instead of falling to 'Other Document').
  return DOC_TYPE_BRANDING[value] ?? DOC_TYPE_BRANDING[normalizeDocTypeValue(value)];
}

/**
 * BACKLOG-0600 — tolerant per-doc extract lookup for `person.extra.extracts`.
 *
 * Extracts are NOW written under the canonical doc-type value (both paths —
 * see buildPatchFromAzureDiId / buildPatchFromNra). Extracts written BEFORE
 * the canonical-key fix are keyed by `nraType` ('passport', 'ssn') on the
 * v3-nra path and by the ROUTE value ('primary-id' / 'secondary-id') on the
 * azure-di-id generic slots. Try canonical first, then the wire id, then any
 * caller-supplied legacy keys (typically the Stage-4 tile id), so in-flight
 * carts / persons keep resolving.
 */
export function extractsForDocType(
  extractsRoot: Record<string, unknown>,
  docTypeValue: string,
  legacyKeys: ReadonlyArray<string> = [],
): Record<string, { value?: unknown }> {
  const key = extractsKeyForDocType(extractsRoot, docTypeValue, legacyKeys);
  return key ? (extractsRoot[key] as Record<string, { value?: unknown }>) : {};
}

/** The `extracts` key extractsForDocType reads for this doc type, or undefined when none holds data. */
export function extractsKeyForDocType(
  extractsRoot: Record<string, unknown>,
  docTypeValue: string,
  legacyKeys: ReadonlyArray<string> = [],
): string | undefined {
  const canonical = normalizeDocTypeValue(docTypeValue);
  const opt = findDocType(canonical);
  const keys: string[] = [canonical];
  if (!keys.includes(docTypeValue)) keys.push(docTypeValue);
  // Legacy v3-nra write key (extracts[nraType], pre-0600).
  if (opt?.mode === 'v3-nra' && opt.nraType && !keys.includes(opt.nraType)) keys.push(opt.nraType);
  for (const k of legacyKeys) {
    if (k && !keys.includes(k)) keys.push(k);
  }
  return keys.find((k) => {
    const e = extractsRoot[k];
    return e != null && typeof e === 'object' && Object.keys(e as object).length > 0;
  });
}

/**
 * Step 4 — Classify Picker options. Returns the canonical F254 1-9 doc-type
 * list (excluding 0 Blank + 9 Lexis Nexis system-only) suitable for the
 * CaptureModal `overrideOptions` prop. Each label embeds the F254 code so
 * the FSR sees the JHA Core Director field mapping inline.
 *
 * Bill 2026-05-28 — per reference_cd_idverifycode_field254_verified_20260527,
 * the canonical FSR-driven set is codes 1-8. The picker is sorted by F254
 * code ascending so the most-common Driver's License + State ID lead.
 */
export interface ClassifyPickerOption {
  value: string;        // DOC_TYPE_OPTIONS.value (route key)
  label: string;        // "1 — Driver's License"
  iconSlug: string;     // brand icon
  idCodeF254: string;   // "1" .. "8"
}

/**
 * Bill 2026-05-28 GAP B — Stage 4 tile-id → Stage 9 canonical DocCode bridge.
 *
 * Stage 4 (PdwStage4DocsGrid) uses cohort-specific tile ids like 'primary-id',
 * 'ssn-card', 'green-card'. Stage 9 (IndividualReviewBody → DocumentSlotsSection
 * + Verify tab) uses the canonical Stage-9 DocCode catalog (GOVT_ID,
 * SSN_CARD, GREEN_CARD, ADDR_PROOF, PASSPORT, VISA, I94, I20, DS2019, EAD,
 * BIRTH_CERT, ITIN_LTR — see DOC_CODE_TO_SPEC in citizenshipDocChecklist.ts +
 * fallbackRequirementsForEntity in DocumentSlotsSection.tsx).
 *
 * Previously the AttachedDoc.docCode was just scanTile.tileId, so Stage 9
 * couldn't recognize Stage 4 captures (tileId="primary-id" ≠ docCode="GOVT_ID").
 * This bridge resolves the canonical docCode from the tile id + the
 * auto-classified doc-type. Stage 9 then sees Stage 4 captures as fulfilling
 * the matching catalog row.
 *
 * Generic slots (primary-id / secondary-id) translate via the classified
 * docTypeValue (us-drivers-license → GOVT_ID, us-passport → PASSPORT, etc.);
 * specific tiles translate by direct tile→code map.
 *
 * GAP I (Bill 2026-05-28) — Secondary slot collision. Individual entityType
 * DocumentRequirements catalog only has GOVT_ID + ADDR_PROOF rows; if both
 * Primary (DL) and Secondary (Passport) capture into GOVT_ID, the
 * attachedDocs storage collides. SECONDARY slot gets a "_2" suffix so the
 * AttachedDocs persist as separate rows for audit + future catalog
 * expansion (GOVT_ID_2). Stage 9 catalog won't render a tile for GOVT_ID_2
 * yet — the capture stays on the person.extra.attachedDocs for retention.
 */
export function tileIdToCanonicalDocCode(
  tileId: string,
  classifiedDocTypeValue?: string,
): string {
  // Generic slots — resolved by classified doc-type.
  if (tileId === 'primary-id' || tileId === 'secondary-id') {
    let baseCode: string;
    switch (classifiedDocTypeValue) {
      case 'us-passport':
      case 'foreign-passport':
        baseCode = 'PASSPORT';
        break;
      case 'green-card':
        baseCode = 'GREEN_CARD';
        break;
      case 'ssn-card':
        baseCode = 'SSN_CARD';
        break;
      case 'birth-cert':
        baseCode = 'BIRTH_CERT';
        break;
      case 'us-drivers-license':
      case 'state-id':
      default:
        // Default to GOVT_ID — Individual/Minor entity-type fallback's primary
        // catalog row. The Stage-9 DocumentSlotsSection seeds GOVT_ID for
        // Individual/Minor when DocumentRequirements isn't seeded yet.
        baseCode = 'GOVT_ID';
        break;
    }
    // GAP I — Secondary slot gets a "_2" suffix so it doesn't clobber the
    // Primary slot's docCode in person.extra.attachedDocs. PASSPORT-as-
    // primary + DL-as-secondary stay as separate audit rows. The Stage 9
    // catalog currently only renders the un-suffixed code, but the
    // attached doc still persists for retention.
    return tileId === 'secondary-id' ? `${baseCode}_2` : baseCode;
  }
  // Specific tiles — direct map. 0600: cohortDocTiles now uses the canonical
  // ids ('foreign-passport' / 'us-visa'); the bare 'passport' / 'visa' rows
  // stay as legacy tolerance for stored tile ids from pre-canonical sessions.
  switch (tileId) {
    case 'passport':         return 'PASSPORT';
    case 'foreign-passport': return 'PASSPORT';
    case 'us-passport':      return 'PASSPORT';
    case 'visa':             return 'VISA';
    case 'us-visa':          return 'VISA';
    case 'i94':           return 'I94';
    case 'i20':           return 'I20';
    case 'ds2019':        return 'DS2019';
    case 'green-card':    return 'GREEN_CARD';
    case 'ead':           return 'EAD';
    case 'ssn-card':      return 'SSN_CARD';
    case 'address-proof': return 'ADDR_PROOF';
    case 'birth-cert':    return 'BIRTH_CERT';
    case 'itin-letter':   return 'ITIN_LTR';
    case 'i797':          return 'I797';
    case 'visa-class-proof': return 'VISA_CLASS_PROOF';
    default:              return tileId.toUpperCase();
  }
}

export function classifyPickerOptions(): ReadonlyArray<ClassifyPickerOption> {
  const rows: ClassifyPickerOption[] = [];
  const seen = new Set<string>();
  for (const [value, branding] of Object.entries(DOC_TYPE_BRANDING)) {
    if (!branding.idCodeF254) continue;
    // De-dupe by F254 code (us-passport + foreign-passport both F254=3; keep
    // the generic 'Passport' representation via us-passport which renders
    // the US-passport icon — FSR can refine via the override). Skip
    // 'primary-id' generic placeholder — it isn't a destination type.
    if (value === 'primary-id') continue;
    if (seen.has(branding.idCodeF254)) continue;
    seen.add(branding.idCodeF254);
    rows.push({
      value,
      label: `${branding.idCodeF254} — ${branding.label}`,
      iconSlug: branding.iconSlug,
      idCodeF254: branding.idCodeF254,
    });
  }
  rows.sort((a, b) => Number(a.idCodeF254) - Number(b.idCodeF254));
  return rows;
}

/**
 * Map an Azure DI `documentType` value (returned by prebuilt-idDocument's
 * raw type field) to one of our doc-type dropdown values, used as the
 * auto-detect default before officer override.
 *
 * Azure DI / our BE returns strings like:
 *   "idDocument.driverLicense"     "idDocument.passport"
 *   "idDocument.usSocialSecurityCard"
 *   "idDocument.residencePermit"   "idDocument.nationalIdentityCard"
 *   "idDocument.stateId"
 *
 * Normalization pipeline:
 *   1. Strip the `idDocument.` (or any vendor) namespace prefix
 *   2. Insert spaces between camelCase / PascalCase tokens
 *   3. Lowercase + collapse separators
 *   4. Pattern-match into our DOC_TYPE_OPTIONS values
 *
 * Bill 2026-05-28 — first attempt dropped only `[_-]`; left the `idDocument.`
 * prefix + camelCase in place, so "idDocument.usSocialSecurityCard" →
 * "iddocument.ussocialsecuritycard" → no pattern matched → 'other'. The
 * cascading effect: autoClassify suggested 'other', the Classify Picker
 * dropdown defaulted to "Other / Unrecognized", drop-a-stack ingest had
 * to fall back to generic slot lookup, and SSN/Green Card auto-mapping
 * broke. This rewrite handles all real BE response shapes.
 */
export function azureDiTypeToDocTypeValue(rawDocumentType: string | undefined): string {
  if (!rawDocumentType) return 'other';
  // 1. Drop the vendor namespace prefix (idDocument.<X> → <X>) — handles
  //    both raw Azure DI camelCase and BE-rebuilt forms.
  const stripped = rawDocumentType.replace(/^id[A-Za-z]*\./i, '');
  // 2. Insert space at every lowercase→Uppercase transition + digit/letter
  //    transitions (camelCase → "camel Case", "F1Visa" → "F 1 Visa").
  const spaced = stripped
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2');
  // 3. Normalize separators + lowercase.
  const norm = spaced.toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();

  // SSN card — Azure DI returns "usSocialSecurityCard" → "us social security card"
  if (norm.includes('social security')) return 'ssn-card';
  // Green Card / Permanent Resident Card (I-551) — Azure DI's "residencePermit"
  // for the I-551 form factor, occasionally "permanentResidentCard".
  if (norm.includes('residence permit') || norm.includes('permanent resident')) return 'green-card';
  if (norm.includes('driver')) return 'us-drivers-license';
  // State ID — covers "stateId", "identityCard", "nationalIdentityCard".
  if (
    norm.includes('state id')
    || norm === 'identity card'
    || norm === 'national identity card'
    || norm.includes('national identity')
  ) {
    return 'state-id';
  }
  if (norm.includes('passport')) return 'us-passport';
  return 'other';
}

/**
 * Map a `topClass` string from the NRA classifier to one of our doc-type
 * dropdown values. The classifier returns labels like "passport", "visa",
 * "i94", "i20", "ds2019", "i797b", "unknown".
 */
export function nraClassToDocTypeValue(topClass: string | undefined): string {
  if (!topClass) return 'other';
  const norm = topClass.toLowerCase().trim();
  switch (norm) {
    case 'passport':   return 'foreign-passport';
    case 'visa':       return 'us-visa';
    case 'i94':        return 'i94';
    case 'i20':        return 'i20';
    case 'ds2019':     return 'ds2019';
    case 'green-card':
    case 'greencard':  return 'green-card';
    case 'ead':        return 'ead';
    case 'ssn':        return 'ssn-card';
    default:           return 'other';
  }
}

// -----------------------------------------------------------------------------
// v0.14.20 — helpers for MRZ persistence + mis-routing detection
// -----------------------------------------------------------------------------

/**
 * Split a MachineReadableZone string returned by Azure DI into [top, bottom].
 * Azure DI typically returns the two MRZ lines separated by a newline; falls
 * back to single-line when only one line was captured.
 */
function splitMrzLines(mrz: string): [string, string | null] {
  if (!mrz) return ['', null];
  const lines = mrz.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return [lines[0] ?? mrz, lines[1] ?? null];
}

/**
 * Compare officer-selected docType against the docType Azure DI / NRA
 * classifier returned. Emits a warning string when they disagree so the
 * FSR can confirm before the wrong-slot extraction lands on the person.
 *
 * Returns null when the two agree (or when detected is missing/unknown).
 *
 * Examples:
 *   expected='us-drivers-license', detected='us-passport' → warning
 *   expected='foreign-passport',  detected='us-passport' → warning (foreign vs US)
 *   expected='i94',               detected='us-visa'      → warning (NRA classifier flagged)
 */
function detectMisrouting(
  expected: string,
  detected: string | undefined,
): string | null {
  if (!detected) return null;
  if (detected === 'other' || detected === 'unknown') return null;
  if (expected === detected) return null;
  // 0600 — the generic Primary / Secondary Photo ID slots ACCEPT any photo-ID
  // subtype by design (prebuilt-idDocument self-classifies); a detected
  // 'us-drivers-license' inside 'primary-id' is correct routing, not a
  // mis-route.
  if (expected === 'primary-id' || expected === 'secondary-id') return null;
  // The classifier conflates us-passport ↔ foreign-passport when only the
  // photo page is presented — call this out as a distinct soft-warning
  // rather than treating it as a clean mis-route.
  if (
    (expected === 'foreign-passport' && detected === 'us-passport') ||
    (expected === 'us-passport' && detected === 'foreign-passport')
  ) {
    return `Selected "${expected}" but document appears to be "${detected}" — confirm passport origin before saving.`;
  }
  return `Selected "${expected}" but document appears to be "${detected}" — rescan or confirm the slot is correct.`;
}

// -----------------------------------------------------------------------------
// Routing pipeline
// -----------------------------------------------------------------------------

export interface RouteCaptureInput {
  docType: string;
  base64: string;
  mimeType: string;
  person: RaoPerson;
  apiClient: ApiClient;
  /** Optional cart context for telemetry on /v3/nra/scan calls. */
  cartId?: string;
  // ── Phase H.1c — upload metadata. When `cartId` + `personRefId` +
  // `officerCode` are all provided, the routing pipeline calls
  // `/v3/documents/upload-attached` after extraction and stamps `fileUrl`,
  // `spItemId`, `sha256` onto the returned AttachedDoc. When any of these
  // are missing the routing pipeline skips the upload (legacy code paths
  // that don't yet thread the cart context still work).
  /** Stable per-person key — usually `person.cifId` or a `personDraftId`. */
  personRefId?: string;
  /** Officer's Core Director officer code (from AuthContext). */
  officerCode?: string;
  /** Optional Teams session id — propagated for audit. */
  sessionId?: string;
  /** Entity type from `derivation.customerType` (Individual/Minor/etc). */
  entityType?: string;
  /**
   * Origin of the bytes. Defaults to `'cip-scan'` for the Phase G/H unified
   * surface. Per-tab capture surfaces (TabSsnCardCapture etc.) pass their
   * own origin if/when they migrate to the upload path.
   */
  sourceType?: UploadAttachedSourceType;
}

export interface RouteCaptureResult {
  /** RaoPerson patch (empty-fields-only — never clobbers officer values). */
  patch: Partial<RaoPerson>;
  /** AttachedDoc emitted regardless of extraction outcome. */
  attachedDoc: AttachedDoc;
  /** Keys to feed onMarkExtracted (excludes `extra`). */
  extractedFieldKeys: ReadonlyArray<keyof RaoPerson>;
  /**
   * Officer-facing summary of what happened ("Routed to Primary ID slot · 7
   * fields auto-filled"). Used by the inline success banner.
   */
  message: string;
  /** Detected raw document type from Azure DI (for the "Detected as" label). */
  detectedRawType?: string;
  /**
   * Bill 2026-10-16 — clockwise quarter-turn that makes the scanned image
   * upright (Azure DI page angle). The capture modal rotates the stored + shown
   * page by it.
   */
  uprightRotationDeg?: number;
  /**
   * v0.14.20 — set when the document Azure DI returned does NOT match the
   * doc-type slot the officer selected. The dialog renders this as an
   * amber confirm-or-rescan banner before the extracted fields are
   * applied to the person. Example trigger: officer drags a Visa into
   * the I-94 slot — `detectedRawType` says "visa", `expected` says "i94".
   */
  mismatchWarning?: string;
  /** Set when the extraction call failed but the doc was still attached. */
  extractionError?: string;
  /**
   * NRA smoke test (Bill 2026-06-12) — stated-vs-extracted conflicts the
   * mapping pass detected (e.g. pre-selected citizenship ≠ document value).
   * Per North-Star §2.6: major → ALERT in the capture modal, FSR picks a
   * side before Apply; the stated value is never silently overwritten.
   */
  conflicts?: ReadonlyArray<CaptureConflict>;
  /**
   * BE `NraScanResponse.needsConfirm` passthrough — field track-keys the
   * server flagged below its confidence floor (incl. the heuristic
   * address-letter extraction's fixed 0.66 rows). The capture modal counts
   * these in the "need confirm" badge and highlights the matching rows so
   * the FSR confirms/corrects them before Apply.
   */
  needsConfirm?: ReadonlyArray<string>;
  /**
   * Phase H.1c — set when `/v3/documents/upload-attached` failed AFTER a
   * successful extraction. The AttachedDoc still lands locally (with
   * `fileUrl=''`) so a retry button can re-attempt the upload.
   */
  uploadError?: string;
  /**
   * Phase H.1c — set when the upload landed cleanly, so the surface can
   * show "Uploaded to ao_Documents · spItemId 12345" alongside the routing
   * message.
   */
  spItemId?: number;
}

/**
 * Build the AttachedDoc record from a base64 payload + slot. Filename embeds
 * the doc-type value + timestamp so multiple captures in the same slot are
 * distinguishable in audit even though only the most recent stays in the
 * dialog's active store.
 */
async function makeAttachedDoc(
  docType: DocTypeOption,
  base64: string,
  mimeType: string,
): Promise<AttachedDoc> {
  const ext =
    mimeType === 'application/pdf' ? 'pdf' :
    mimeType === 'image/png'        ? 'png' :
    mimeType === 'image/tiff'       ? 'tif' :
    'jpg';
  const ts = Date.now();
  // v0.3.8 — Teams iframe sandbox refuses to render `data:` URLs in <img>,
  // which is why every thumbnail in the wizard + Documents tab was a broken
  // square. Convert image bytes to a blob: URL once at capture time so all
  // downstream consumers (DocCaptureSurface, IndividualPersonWizardDocMatrix
  // tiles, DocumentSlotsSection rows, DocumentLightbox) get a URL that
  // actually renders.
  //
  // For PDFs we render the FIRST PAGE via pdf.js + canvas → JPEG blob URL,
  // so the thumbnail shows real document content (not just a generic icon).
  // pdf.js worker is lazy-loaded the first time a PDF lands, so the bundle
  // cost only hits when needed.
  let imageDataUrl = '';
  if (mimeType.startsWith('image/')) {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      imageDataUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
    } catch {
      imageDataUrl = '';
    }
  } else if (mimeType === 'application/pdf') {
    imageDataUrl = await pdfFirstPageThumbnailFromBase64(base64, { width: 240 });
  }
  return {
    docCode: docType.slot,
    source: 'cip-scan',
    filename: `${docType.value}-${ts}.${ext}`,
    contentType: mimeType,
    sizeBytes: estimateBase64Size(base64),
    imageDataUrl,
    fileUrl: '',
    capturedAt: new Date(ts).toISOString(),
  };
}

/**
 * Build the empty-fields-only RaoPerson patch from an Azure DI ID extraction.
 * Mirrors the demographic-blank-auto-fill rule from IdCapturePanel +
 * extends it to write CIP fields (idType / idNumber / idIssuer /
 * idIssueDate / idExpiryDate) onto person.extra.
 *
 * Cross-contamination fix (Bill 2026-05-28): `patch.extra` contains ONLY the
 * keys this scan ADDED (no spread of person.extra). The caller's onChange
 * MUST deep-merge `extra` so existing CIP / per-type fields aren't lost.
 * Display layer (scanRouter.patchToFields) reads `patch.extra` directly so
 * the review pane shows ONLY fields extracted on THIS scan.
 */
function buildPatchFromAzureDiId(
  person: RaoPerson,
  ex: ExtractedIdFields,
  docTypeKey: string,
): { patch: Partial<RaoPerson>; keys: Array<keyof RaoPerson> } {
  const out: Partial<RaoPerson> = {};
  const keys: Array<keyof RaoPerson> = [];

  if (!person.firstName && ex.firstName)   { out.firstName  = ex.firstName;  keys.push('firstName'); }
  if (!person.middleName && ex.middleName) { out.middleName = ex.middleName; keys.push('middleName'); }
  if (!person.lastName && ex.lastName)     { out.lastName   = ex.lastName;   keys.push('lastName'); }
  if (!person.dob && ex.dob)               { out.dob        = ex.dob;        keys.push('dob'); }
  if (!person.addr && ex.addr)             { out.addr       = ex.addr;       keys.push('addr'); }
  if (!person.city && ex.city)             { out.city       = ex.city;       keys.push('city'); }
  if (!person.state && ex.state)           { out.state      = ex.state;      keys.push('state'); }
  if (!person.zip && ex.zip)               { out.zip        = ex.zip;        keys.push('zip'); }
  if (!person.gender && ex.gender)         { out.gender     = ex.gender;     keys.push('gender'); }

  // CIP fields → person.extra (mirrors IdCapturePanel.buildCipPatch primary
  // slot). These don't go on `keys` because they aren't first-class
  // RaoPerson keys; the existing CIP section handles its own AUTO-badge
  // story via FieldComparisonDialog accept rows. G.1 keeps that arrangement.
  //
  // CROSS-CONTAMINATION FIX: extraPatch starts EMPTY (was spread of
  // existingExtra — caused the per-tile DL scan's extras to bleed into a
  // subsequent SSN / Green Card scan's review pane). Caller deep-merges.
  const existingExtra = (person.extra ?? {}) as Record<string, unknown>;
  const extraPatch: Record<string, unknown> = {};
  if (ex.idTypeCode    && !existingExtra.idType)        { extraPatch.idType       = ex.idTypeCode;    }
  if (ex.idIssuer      && !existingExtra.idIssuer)      { extraPatch.idIssuer     = ex.idIssuer;      }
  if (ex.idNumber      && !existingExtra.idNumber)      { extraPatch.idNumber     = ex.idNumber;      }
  if (ex.idIssueDate   && !existingExtra.idIssueDate)   { extraPatch.idIssueDate  = ex.idIssueDate;   }
  if (ex.idExpiryDate  && !existingExtra.idExpiryDate)  { extraPatch.idExpiryDate = ex.idExpiryDate;  }

  // BACKLOG-0601 — per-type extract store. ExtractedIdFields is flat strings +
  // a confidence map; project the non-null values into the {value,confidence}
  // shape under person.extra.extracts.<docType> (permanent audit; feeds §2
  // cross-ref + confirm-to-CIF). Written on every scan, scoped to docTypeKey,
  // so the new extracts.<docType> object replaces (not merges) prior. The
  // caller's deep-merge preserves OTHER doc-types under extracts.* via the
  // same `extracts` key sub-merge in PdwShell.onChange.
  const azConf = (ex.confidence ?? {}) as Record<string, number>;
  const azExtract: Record<string, RetainedExtractField> = {};
  for (const [k, v] of Object.entries(ex)) {
    if (k === 'confidence' || k === 'rawDocumentType') continue;
    if (typeof v === 'string' && v) azExtract[k] = { value: v, confidence: Number(azConf[k] ?? 0) };
  }
  // Per-doc-type extract slot. Caller's deep-merge for `extracts` honors
  // OTHER doc-types under person.extra.extracts.* (sub-key merge).
  extraPatch.extracts = { [docTypeKey]: azExtract };
  out.extra = extraPatch;

  return { patch: out, keys };
}

/**
 * Build the empty-fields-only RaoPerson patch from a /v3/nra/scan response.
 *
 * TODO Phase G.5: dedupe with IndividualNraDocumentScanTabs.mapToPersonPatch.
 * That sibling currently writes WITHOUT the empty-fields-only guard (it
 * relies on the officer-driven "Apply" button when needsConfirm is non-zero).
 * For G.1 we re-state the mapping here with the guard so the unified surface
 * never clobbers officer values, even when extraction lands high-confidence.
 */
function buildPatchFromNra(
  person: RaoPerson,
  fields: import('@retail-ops/api-client').NraScanFields,
  docType: NraDocumentType,
  // 0600 — canonical DOC_TYPE_OPTIONS value used as the extracts store key
  // (the wire `docType`/nraType still drives the field-family switch below).
  extractsKey: string,
): { patch: Partial<RaoPerson>; keys: Array<keyof RaoPerson>; conflicts: CaptureConflict[] } {
  const patch: Partial<RaoPerson> = {};
  const keys: Array<keyof RaoPerson> = [];
  const extraPatch: Record<string, unknown> = {};
  const conflicts: CaptureConflict[] = [];
  let extraTouched = false;

  // Universal fields — only when blank.
  //
  // v0.4.0 — name parsing for foreign passports. Azure DI's prebuilt model
  // often returns the entire passport name in BOTH the FirstName and LastName
  // slots for non-US issuers (Malaysian, Indonesian, Indian, Arabic). PCB
  // convention splits on patronymic markers ("Bin", "Binti", "Ibn") or
  // last-token-as-surname when no marker is present. parseForeignName
  // handles both well-formed and duplicated AI output.
  const parsedName = parseForeignName({
    fullName: fields.fullName?.value ?? null,
    firstName: fields.firstName?.value ?? null,
    middleName: fields.middleName?.value ?? null,
    lastName: fields.lastName?.value ?? null,
  });
  if (!person.firstName && parsedName.first) {
    patch.firstName = parsedName.first;
    keys.push('firstName');
  }
  if (!person.middleName && parsedName.middle) {
    patch.middleName = parsedName.middle;
    keys.push('middleName');
  }
  if (!person.lastName && parsedName.last) {
    patch.lastName = parsedName.last;
    keys.push('lastName');
  }
  if (!person.dob && fields.dateOfBirth?.value) {
    patch.dob = isoDate(fields.dateOfBirth.value);
    keys.push('dob');
  }
  if (fields.countryOfCitizenship?.value) {
    // v0.3.5 — Azure DI returns ISO 3166-1 alpha-3 ("MYS"); the
    // CountrySelect dropdown uses alpha-2 ("MY"). Normalize so the auto-fill
    // actually lights the dropdown instead of falling back to "(select)".
    // 2026-06-12 — name-tolerant: the I-20 prints the full country name.
    const extractedIso2 = citizenshipToIso2(fields.countryOfCitizenship.value);
    const stated = (person.countryOfCitizenship ?? '').trim();
    if (!stated) {
      // Patch-when-empty — unchanged behavior.
      patch.countryOfCitizenship = extractedIso2;
      keys.push('countryOfCitizenship');
    } else if (stated.toUpperCase() !== extractedIso2.toUpperCase()) {
      // NRA smoke test (Bill 2026-06-12) — the FSR pre-selected a citizenship
      // country and the document reads a different one. Per North-Star brief
      // §2.6 tiering this is a MAJOR conflict: ALERT + FSR picks a side in
      // the capture modal. NEVER silently overwrite the stated value.
      const rawRead = fields.countryOfCitizenship.value.trim();
      conflicts.push({
        field: 'countryOfCitizenship',
        label: 'Country of citizenship',
        stated: stated.toUpperCase(),
        extracted: rawRead.toUpperCase() === extractedIso2.toUpperCase()
          ? extractedIso2
          : `${extractedIso2} (document reads "${rawRead}")`,
        extractedPatchValue: extractedIso2,
        severity: 'major',
      });
    }
  }
  // v0.4.0 — Sex / Gender. Server returns "M" / "F" / "X"; RaoPerson.gender
  // expects "Male" / "Female" / ''. Map to the canonical form. Officer can
  // override via the dropdown.
  if (!person.gender && fields.sex?.value) {
    const raw = fields.sex.value.trim().toUpperCase();
    const mapped: 'Male' | 'Female' | '' =
      raw.startsWith('M') ? 'Male' :
      raw.startsWith('F') ? 'Female' :
      '';
    if (mapped) {
      patch.gender = mapped;
      keys.push('gender');
    }
  }

  // v0.3.5 — read existing extra once so each case can splat into it without
  // racing the universal block above.
  const existingExtra = (person.extra ?? {}) as Record<string, unknown>;

  switch (docType) {
    case 'primary-id': {
      // BACKLOG-0600 — US-Citizen / Permanent-Resident PRIMARY photo ID. Name /
      // dob / citizenship / gender are handled by the universal block above.
      // Here: address (empty-only) + coded CIP (F254-258 → extra.id*), mirroring
      // buildPatchFromAzureDiId so the photo-ID paths converge.
      // Street line 2 (apartment / unit) joins the street — one delivery line (Bill 2026-10-06).
      if (!person.addr && fields.addressLine1?.value) { patch.addr = joinStreetLines(fields.addressLine1.value, fields.addressLine2?.value); keys.push('addr'); }
      if (!person.city && fields.addressCity?.value) { patch.city = fields.addressCity.value; keys.push('city'); }
      if (!person.state && fields.addressState?.value) { patch.state = fields.addressState.value; keys.push('state'); }
      if (!person.zip && fields.addressZip?.value) { patch.zip = fields.addressZip.value; keys.push('zip'); }
      if (fields.pcbIdType?.value && !existingExtra.idType) { extraPatch.idType = fields.pcbIdType.value; extraTouched = true; }
      if (fields.pcbIdNumber?.value && !existingExtra.idNumber) { extraPatch.idNumber = fields.pcbIdNumber.value; extraTouched = true; }
      if (fields.pcbIdIssuer?.value && !existingExtra.idIssuer) { extraPatch.idIssuer = fields.pcbIdIssuer.value; extraTouched = true; }
      if (fields.pcbIdIssueDate?.value && !existingExtra.idIssueDate) { extraPatch.idIssueDate = isoDate(fields.pcbIdIssueDate.value); extraTouched = true; }
      if (fields.pcbIdExpiryDate?.value && !existingExtra.idExpiryDate) { extraPatch.idExpiryDate = isoDate(fields.pcbIdExpiryDate.value); extraTouched = true; }
      break;
    }

    case 'passport':
      // NRA smoke test (Bill 2026-06-12) — the old mapping copied the passport
      // number into `fornTaxId`. WRONG: a passport does not carry a foreign
      // tax ID. Passport captures now leave fornTaxId untouched (it stays a
      // manual / W-8-driven field).
      if (!person.foreignPassportNumber && fields.foreignPassportNumber?.value) {
        patch.foreignPassportNumber = fields.foreignPassportNumber.value;
        keys.push('foreignPassportNumber');
      }
      if (!person.foreignPassportCountry && fields.foreignPassportCountry?.value) {
        patch.foreignPassportCountry = normalizeIso2(fields.foreignPassportCountry.value);
        keys.push('foreignPassportCountry');
      }
      if (fields.foreignPassportIssueDt?.value && !existingExtra.passportIssueDt) {
        extraPatch.passportIssueDt = isoDate(fields.foreignPassportIssueDt.value); extraTouched = true;
      }
      if (fields.foreignPassportExpDt?.value && !existingExtra.passportExpDt) {
        extraPatch.passportExpDt = isoDate(fields.foreignPassportExpDt.value); extraTouched = true;
      }
      // v0.3.5 — populate the canonical CIP slot (Field 254-258) so the CIP
      // tab stops showing empty. ID type code 3 = Passport in PCB's Core
      // Director enum (matches IdCapturePanel CIP-primary mapping).
      if (fields.foreignPassportNumber?.value && !existingExtra.idNumber) {
        extraPatch.idNumber = fields.foreignPassportNumber.value; extraTouched = true;
      }
      // F256 CIP ID Issuer — FULL country name for foreign passports (Bill
      // ruling 2026-06-12): F256 allows 40 alphanumeric and spot-checked
      // existing NRA CIFs carry the full name (e.g. "India"), NOT the raw
      // "IND"/"IN" code this previously wrote. Resolves through the
      // CountryCatalog (resolveCountryName); unresolvable values keep the
      // old normalizeIso2 behavior so nothing is lost.
      //
      // Core Director country destinations for the commit-pipeline follow-up
      // (documented only — CD writes are NOT wired in this lane):
      //   F949 Country Code (2-character — catalog cdCode), F355 Country of
      //   Birth, F356 Citizenship Country, F357 Dual Citizenship, and
      //   F23 Call Class = 3 Foreign/Non-US Citizen.
      if (fields.foreignPassportCountry?.value && !existingExtra.idIssuer) {
        const rawCountry = fields.foreignPassportCountry.value;
        extraPatch.idIssuer = resolveCountryName(rawCountry) ?? normalizeIso2(rawCountry);
        extraTouched = true;
      }
      if (fields.foreignPassportIssueDt?.value && !existingExtra.idIssueDate) {
        extraPatch.idIssueDate = isoDate(fields.foreignPassportIssueDt.value); extraTouched = true;
      }
      if (fields.foreignPassportExpDt?.value && !existingExtra.idExpiryDate) {
        extraPatch.idExpiryDate = isoDate(fields.foreignPassportExpDt.value); extraTouched = true;
      }
      if (!existingExtra.idType) {
        extraPatch.idType = '3'; extraTouched = true; // Passport
      }
      // v0.14.20 — persist MRZ (top + bottom) so LexisNexis InstantID can
      // consume it and cross-doc conflict detection can run against it.
      // Azure DI returns MRZ as a single string with the two lines joined
      // by a newline; we keep both forms — raw + parsed pair.
      if (fields.machineReadableZone?.value && !existingExtra.passportMrz) {
        const raw = fields.machineReadableZone.value;
        extraPatch.passportMrz = raw; extraTouched = true;
        const [top, bottom] = splitMrzLines(raw);
        if (top)    { extraPatch.passportMrzTop = top; extraTouched = true; }
        if (bottom) { extraPatch.passportMrzBottom = bottom; extraTouched = true; }
      }
      break;

    case 'visa':
      if (fields.visaType?.value) {
        // v0.4.0 — pcb-visa-v2 returns compact codes ("F1", "H1B").
        // The dropdown uses hyphenated form ("F-1", "H-1B").
        const normalizedVisa = normalizeVisaType(fields.visaType.value);
        if (!person.visaType) {
          patch.visaType = normalizedVisa;
          keys.push('visaType');
        }
        // v0.4.2 — IndividualCitizenshipSection (Origin tab) reads visaType
        // from `person.extra.visaType`, not the top-level field. Mirror to
        // both so the dropdown on the Origin tab actually lights up — the
        // deeper IndividualNraSection uses the top-level. Both surfaces
        // need the value or the AUTO badge lies.
        if (!existingExtra.visaType) {
          extraPatch.visaType = normalizedVisa;
          extraTouched = true;
        }
      }
      if (!person.visaExpDt && fields.visaExpDt?.value) {
        patch.visaExpDt = fields.visaExpDt.value; keys.push('visaExpDt');
      }
      if (fields.visaIssueDt?.value)       { extraPatch.visaIssueDt = fields.visaIssueDt.value; extraTouched = true; }
      if (fields.visaIssuePost?.value)     { extraPatch.visaIssuePost = fields.visaIssuePost.value; extraTouched = true; }
      if (fields.visaEntries?.value)       { extraPatch.visaEntries = fields.visaEntries.value; extraTouched = true; }
      if (fields.visaAnnotation?.value)    { extraPatch.visaAnnotation = fields.visaAnnotation.value; extraTouched = true; }
      if (fields.visaControlNumber?.value) { extraPatch.visaControlNumber = fields.visaControlNumber.value; extraTouched = true; }
      // Bill smoke #2 (2026-06-12) — the visa's OWN document number = the
      // red-printed foil number (W-prefixed on Bill's sample). BE maps it
      // from the WNumber leaf (VisaNumber secondary); the REFERENCED
      // passport number stays a cross-ref below and never poses as the
      // visa's document number.
      if (fields.visaDocumentNumber?.value) { extraPatch.visaDocumentNumber = fields.visaDocumentNumber.value; extraTouched = true; }
      // v0.14.20 — persist visa-side MRZ + the passport-number printed on
      // the visa foil. The latter is the cross-ref that flags counterfeit
      // when the FSR later scans the actual passport and the numbers don't
      // match. NewWithVisa() factory routes Pull("PassportNumber") into
      // ForeignPassportNumber, so we read that field here.
      if (fields.machineReadableZone?.value && !existingExtra.visaMrz) {
        const raw = fields.machineReadableZone.value;
        extraPatch.visaMrz = raw; extraTouched = true;
        const [top, bottom] = splitMrzLines(raw);
        if (top)    { extraPatch.visaMrzTop = top; extraTouched = true; }
        if (bottom) { extraPatch.visaMrzBottom = bottom; extraTouched = true; }
      }
      if (fields.foreignPassportNumber?.value && !existingExtra.visaPassportNumberCrossRef) {
        extraPatch.visaPassportNumberCrossRef = fields.foreignPassportNumber.value;
        extraTouched = true;
      }
      break;

    case 'i94':
      if (!person.i94Number && fields.i94Number?.value) {
        patch.i94Number = fields.i94Number.value; keys.push('i94Number');
      }
      // i94ExpirationDate (BE 2026-06-12) — dedicated expiration leaf,
      // distinct from AdmitUntil (which may carry the "D/S" duration-of-
      // status literal — Bill's "I-94 expiration: null" smoke finding).
      // Preferred source for person.i94ExpDt; non-D/S AdmitUntil below
      // stays as the fallback.
      if (!person.i94ExpDt && fields.i94ExpirationDate?.value) {
        patch.i94ExpDt = isoDate(fields.i94ExpirationDate.value); keys.push('i94ExpDt');
      }
      if (fields.i94AdmitUntil?.value) {
        if (fields.i94AdmitUntil.value.toUpperCase() === 'D/S') {
          extraPatch.i94AdmitUntil = 'D/S'; extraTouched = true;
        } else {
          if (!person.i94ExpDt && !patch.i94ExpDt) { patch.i94ExpDt = isoDate(fields.i94AdmitUntil.value); keys.push('i94ExpDt'); }
          extraPatch.i94AdmitUntil = isoDate(fields.i94AdmitUntil.value); extraTouched = true;
        }
      }
      if (fields.i94AdmitClass?.value) { extraPatch.i94AdmitClass = fields.i94AdmitClass.value; extraTouched = true; }
      if (fields.i94EntryDate?.value)  { extraPatch.i94EntryDate = isoDate(fields.i94EntryDate.value); extraTouched = true; }
      if (fields.i94EntryPort?.value)  { extraPatch.i94EntryPort = fields.i94EntryPort.value; extraTouched = true; }
      // NRA smoke test (Bill 2026-06-12) — the I-94 carries the underlying
      // travel-document (passport) number; the BE maps it onto
      // fields.foreignPassportNumber (NraDocumentScanService MapCuLeavesToNra
      // "i94" → DocumentNumber leaf). Surface as a cross-ref — mirroring the
      // visa path's visaPassportNumberCrossRef — so the FSR can compare it
      // against the captured passport. Never writes the passport slot itself.
      if (fields.foreignPassportNumber?.value && !existingExtra.i94PassportNumberCrossRef) {
        extraPatch.i94PassportNumberCrossRef = fields.foreignPassportNumber.value;
        extraTouched = true;
      }
      break;

    case 'i20':
      if (fields.sevisId?.value)         { extraPatch.sevisId = fields.sevisId.value; extraTouched = true; }
      if (fields.schoolName?.value)      { extraPatch.schoolName = fields.schoolName.value; extraTouched = true; }
      if (fields.schoolCode?.value)      { extraPatch.schoolCode = fields.schoolCode.value; extraTouched = true; }
      if (fields.programStartDt?.value)  { extraPatch.programStartDt = fields.programStartDt.value; extraTouched = true; }
      if (fields.programEndDt?.value)    { extraPatch.programEndDt = fields.programEndDt.value; extraTouched = true; }
      if (fields.educationLevel?.value)  { extraPatch.educationLevel = fields.educationLevel.value; extraTouched = true; }
      if (fields.major1?.value)          { extraPatch.major1 = fields.major1.value; extraTouched = true; }
      if (fields.major1CipCode?.value)   { extraPatch.major1CipCode = fields.major1CipCode.value; extraTouched = true; }
      if (fields.formIssueReason?.value) { extraPatch.formIssueReason = fields.formIssueReason.value; extraTouched = true; }
      // v0.3.7 — Work/School auto-fill from I-20. F-1/F-2/M-1 students treat
      // the school as their primary affiliation; employmentStatus = Student
      // and the school name lands in Employer. NAICS 611310 = Colleges,
      // Universities, and Professional Schools (per BLS NAICS catalog).
      // Empty-fields-only guard so we never clobber officer-typed values.
      //
      // Core Director destinations (Bill 2026-06-12, sevis-school-catalog-
      // runbook §0): F141 Employer (15 chars) + F313 Occupation (30 chars).
      if (!person.employmentStatus) {
        patch.employmentStatus = 'Student';
        keys.push('employmentStatus');
      }
      if (!person.employer && fields.schoolName?.value) {
        // NRA smoke test (Bill 2026-06-12) — use the EXTRACTED school name
        // verbatim. The prior abbreviateUniversity pass turned a clean 96%
        // "University of Missouri" extract into "Mizzou" on the person —
        // wrong per Bill. The Work/School field is editable, so any length
        // trimming for the Core Director Employer field is the FSR's call
        // (commit-side length validation is a separate concern).
        //
        // BACKLOG-0762 — this extract-verbatim value is the FALLBACK: when
        // the SEVIS catalog lookup (applyI20SchoolCatalogLookup, called by
        // routeCaptureInner after this mapping pass) resolves the extracted
        // SchoolCode, it refines this to the catalog's campusName and stores
        // the server-computed ≤15-char `f141Employer` (CD F141 Employer
        // value — commonName15 ?? word-boundary truncation; never re-derived
        // client-side) at extra.f141Employer. Lookup failure keeps this
        // extracted name — non-fatal by doctrine.
        patch.employer = fields.schoolName.value;
        keys.push('employer');
      }
      if (!person.occupation) {
        // CD F313 Occupation (30 chars) ← the constant 'Student' on the
        // student path (Bill 2026-06-12; client-side, not catalog-sourced).
        patch.occupation = 'Student';
        keys.push('occupation');
      }
      if (!person.naicsCode) {
        patch.naicsCode = '611310';
        keys.push('naicsCode');
      }
      break;

    case 'ds2019':
      if (fields.sevisId?.value)                 { extraPatch.sevisId = fields.sevisId.value; extraTouched = true; }
      if (fields.programSponsorName?.value)      { extraPatch.programSponsorName = fields.programSponsorName.value; extraTouched = true; }
      if (fields.programNumber?.value)           { extraPatch.programNumber = fields.programNumber.value; extraTouched = true; }
      if (fields.exchangeVisitorCategory?.value) { extraPatch.exchangeVisitorCategory = fields.exchangeVisitorCategory.value; extraTouched = true; }
      if (fields.fieldCode?.value)               { extraPatch.fieldCode = fields.fieldCode.value; extraTouched = true; }
      if (fields.twoYearResidenceFlag?.value)    { extraPatch.twoYearResidenceFlag = fields.twoYearResidenceFlag.value; extraTouched = true; }
      // v0.3.7 — Work/School auto-fill from DS-2019. The exchangeVisitorCategory
      // discriminates Student-flavor (Student Bachelors/Masters/Doctorate)
      // from Worker-flavor (Research Scholar / Professor / Specialist /
      // Au Pair / Camp Counselor / Trainee / Intern / Government Visitor)
      // — Student → employmentStatus='Student', everything else
      // → 'Employed'. Sponsor lands in Employer. Occupation = the cleaned
      // category string. NAICS 611310 (educational) when student;
      // not auto-set for research/work flavor since the sponsor's NAICS
      // varies (research lab, hospital, government agency, etc.).
      //
      // Core Director destinations (Bill 2026-06-12): Employer → F141
      // Employer (15 chars — commit-side length handling; the SEVIS-catalog
      // f141Employer shortcut is I-20-only, DS-2019 sponsors aren't schools);
      // Occupation → F313 Occupation (30 chars — 'Student' constant on the
      // student flavor, title-cased category otherwise).
      {
        const rawCat = fields.exchangeVisitorCategory?.value?.toUpperCase().trim() ?? '';
        const isStudentCat = rawCat.startsWith('STUDENT');
        if (!person.employmentStatus) {
          patch.employmentStatus = isStudentCat ? 'Student' : 'Employed';
          keys.push('employmentStatus');
        }
        if (!person.employer && fields.programSponsorName?.value) {
          // NRA smoke test (Bill 2026-06-12) — same rule as the I-20 path:
          // use the EXTRACTED sponsor name verbatim (no abbreviation table).
          // The Work/School field is editable; length trimming for the Core
          // Director Employer field is the FSR's call at review.
          patch.employer = fields.programSponsorName.value;
          keys.push('employer');
        }
        if (!person.occupation && fields.exchangeVisitorCategory?.value) {
          // Convert "RESEARCH SCHOLAR" → "Research Scholar".
          const titled = fields.exchangeVisitorCategory.value
            .toLowerCase()
            .replace(/\b\w/g, (m) => m.toUpperCase());
          patch.occupation = titled;
          keys.push('occupation');
        }
        if (isStudentCat && !person.naicsCode) {
          patch.naicsCode = '611310';
          keys.push('naicsCode');
        }
      }
      break;

    case 'green-card':
      if (fields.greenCardANumber?.value)        { extraPatch.greenCardANumber = fields.greenCardANumber.value; extraTouched = true; }
      if (fields.greenCardCategory?.value)       { extraPatch.greenCardCategory = fields.greenCardCategory.value; extraTouched = true; }
      if (fields.greenCardExpDt?.value)          { extraPatch.greenCardExpDt = fields.greenCardExpDt.value; extraTouched = true; }
      if (fields.greenCardResidentSince?.value)  { extraPatch.greenCardResidentSince = fields.greenCardResidentSince.value; extraTouched = true; }
      break;

    case 'ead':
      if (fields.eadANumber?.value)  { extraPatch.eadANumber = fields.eadANumber.value; extraTouched = true; }
      if (fields.eadCategory?.value) { extraPatch.eadCategory = fields.eadCategory.value; extraTouched = true; }
      if (fields.eadExpDt?.value)    { extraPatch.eadExpDt = fields.eadExpDt.value; extraTouched = true; }
      break;

    case 'ssn':
      if (fields.ssnNumber?.value) {
        const digits = fields.ssnNumber.value.replace(/\D/g, '');
        if (digits.length === 9 && !person.taxId) {
          patch.taxId = digits;
          keys.push('taxId');
        }
      }
      break;

    case 'address-proof':
      // v0.3.6 — utility bill / lease / employer letter. Server runs
      // prebuilt-invoice + ParseAddressComponents to break the composite
      // CustomerAddress / BillingAddress into 4 components. Empty-fields-only
      // guard so we never clobber officer-typed values.
      if (!person.addr && fields.addressLine1?.value) {
        patch.addr = joinStreetLines(fields.addressLine1.value, fields.addressLine2?.value);
        keys.push('addr');
      }
      if (!person.city && fields.addressCity?.value) {
        patch.city = fields.addressCity.value;
        keys.push('city');
      }
      if (!person.state && fields.addressState?.value) {
        patch.state = fields.addressState.value.toUpperCase();
        keys.push('state');
      }
      if (!person.zip && fields.addressZip?.value) {
        patch.zip = fields.addressZip.value;
        keys.push('zip');
      }
      break;
  }

  // BACKLOG-0601 — per-type extract store. Retain the raw NraScanFields under
  // person.extra.extracts.<docType> (permanent audit; the producer the §2
  // cross-ref + confirm-to-CIF + NRA-Analysis-Document spine consumes). Always
  // written, even when no canonical slot changed.
  //
  // CROSS-CONTAMINATION FIX (Bill 2026-05-28): patch.extra contains ONLY
  // keys this scan added (no spread of existingExtra). Caller's onChange
  // deep-merges so prior CIP / per-type fields aren't lost; `extracts` is
  // sub-key merged so other doc-types' extracts stay alongside this one.
  //
  // 0600 — keyed by the CANONICAL doc-type value (e.g. 'foreign-passport',
  // 'ssn-card'), NOT the wire nraType ('passport', 'ssn'); readers use
  // extractsForDocType which still tolerates the legacy nraType keys.
  extraPatch.extracts = { [extractsKey]: serializeExtract(fields as unknown as Record<string, unknown>) };
  patch.extra = extraPatch;
  void extraTouched;
  void existingExtra; // referenced by per-case `!existingExtra.X` guards above
  return { patch, keys, conflicts };
}

/**
 * BACKLOG-0762 — I-20 → SEVIS school-catalog lookup (alpha.171 routes;
 * docs/operations/sevis-school-catalog-runbook.md §4).
 *
 * Runs AFTER buildPatchFromNra's i20 mapping pass: resolves the extracted
 * `SchoolInformation.SchoolCode` against GET /v3/config/schools/{campusCode}
 * and, on a hit, refines the patch IN PLACE:
 *
 *   - extra.schoolName       ← catalog `campusName` (campus-level display
 *                              truth; raw extract retained under extracts.i20)
 *   - extra.schoolParentName ← catalog `schoolName` (secondary/parent text)
 *   - extra.schoolCampusCode ← catalog `campusCode` (normalized SEVIS code)
 *   - extra.schoolCommonName15 ← curated short name when present
 *   - extra.f141Employer     ← catalog `f141Employer` — the Core Director
 *                              **F141 Employer (15 chars)** value, SERVER-
 *                              computed (commonName15 ?? word-boundary
 *                              truncation). Single source of truth — never
 *                              re-derived client-side. (CD F313 Occupation =
 *                              'Student' is written by the i20 mapping pass.)
 *   - patch.employer         ← `campusName` (only when the mapping pass was
 *                              already auto-filling it, i.e. person.employer
 *                              blank — officer-typed values stay untouched)
 *
 * Validation advisories (returned as `warnings`, rendered through the
 * existing amber `mismatchWarning` confirm banner — judgment call per the
 * lane brief: the CaptureConflict pick-a-side pattern doesn't fit because
 * there's no single person-field to pick; this is certify-vs-class advice):
 *   - extracted/stated class F-1/F-2 at a campus with fVisa=false, or
 *     M-1/M-2 at mVisa=false → "not SEVP-certified for this visa class"
 *   - isActive=false → "no longer on the SEVP certified list (last seen …)"
 *
 * FAILURE MODES (all non-fatal per the auto-extract-no-gating doctrine):
 * 404 SCHOOL_NOT_FOUND / network / 5xx → swallow, keep the extracted school
 * name, return a `note` for the routing message. Never throws.
 */
async function applyI20SchoolCatalogLookup(
  apiClient: ApiClient,
  person: RaoPerson,
  fields: import('@retail-ops/api-client').NraScanFields,
  patch: Partial<RaoPerson>,
  keys: Array<keyof RaoPerson>,
): Promise<{ warnings: string[]; note?: string }> {
  const rawCode = fields.schoolCode?.value?.trim().toUpperCase() ?? '';
  if (rawCode.length === 0) return { warnings: [] };

  let school: import('@retail-ops/api-client').SevisSchoolRow;
  try {
    const resp = await apiClient.retail.config.schools.byCode(rawCode);
    if (!resp.success || !resp.data) {
      return { warnings: [], note: 'school code not matched in the SEVP catalog — extracted school name kept' };
    }
    school = resp.data;
  } catch {
    // 404 (SCHOOL_NOT_FOUND) + transport errors land here — non-fatal.
    return { warnings: [], note: 'school catalog lookup unavailable — extracted school name kept' };
  }

  const extraPatch = (patch.extra ?? {}) as Record<string, unknown>;
  extraPatch.schoolCampusCode = school.campusCode;
  extraPatch.schoolName = school.campusName;
  extraPatch.schoolParentName = school.schoolName;
  if (school.commonName15) extraPatch.schoolCommonName15 = school.commonName15;
  // CD F141 Employer (15 chars, Bill 2026-06-12) — server-computed value.
  extraPatch.f141Employer = school.f141Employer;
  patch.extra = extraPatch;

  // Refine the auto-filled Work/School display value. Bill 2026-06-12 (v0.47.3
  // smoke): the visible School field should BE the Core Director F141 value
  // ("Missouri S&T"), not the long campus name — F141 is what actually lands
  // on the CIF, and the FSR reads the field as that destination. The full
  // campus name stays at extra.schoolName for reference/audit. Empty-fields-
  // only doctrine holds: we only touch patch.employer when person.employer
  // was blank.
  if (!person.employer) {
    patch.employer = school.f141Employer || school.campusName;
    if (!keys.includes('employer')) keys.push('employer');
  }

  const warnings: string[] = [];

  // F/M certification mismatch — class read from the captured visa stamp
  // (person.visaType, normalizeVisaType-normalized; Origin-tab mirror as
  // fallback). Unknown class → no check (never guess).
  const extra = (person.extra ?? {}) as Record<string, unknown>;
  const mirroredClass = typeof extra.visaType === 'string' ? extra.visaType : undefined;
  const visaClass = normalizeVisaType(person.visaType ?? mirroredClass ?? '');
  const isFClass = visaClass === 'F-1' || visaClass === 'F-2';
  const isMClass = visaClass === 'M-1' || visaClass === 'M-2';
  if (isFClass && !school.fVisa) {
    warnings.push(
      `I-20 school "${school.campusName}" is not SEVP-certified for ${visaClass} (campus is certified for M visas only) — confirm the document before continuing.`,
    );
  } else if (isMClass && !school.mVisa) {
    warnings.push(
      `I-20 school "${school.campusName}" is not SEVP-certified for ${visaClass} (campus is certified for F visas only) — confirm the document before continuing.`,
    );
  }

  if (!school.isActive) {
    warnings.push(
      `"${school.campusName}" is no longer on the SEVP certified-school list (last seen ${school.sourceListDate || 'unknown date'}) — confirm the I-20 is current.`,
    );
  }

  return { warnings };
}

/**
 * Phase H.1c — call `/v3/documents/upload-attached` and stamp the response
 * onto the AttachedDoc. Returns `{ ok, error?, spItemId? }` so the caller
 * can compose the user-facing message. Mutates `attachedDoc` IN PLACE
 * (sha256 / fileUrl / spItemId) when the upload lands; on failure leaves
 * `fileUrl=''` so the dialog's retry path can re-attempt later.
 *
 * Skips silently (returns `{ ok: true }` with no fields) when the caller
 * didn't thread the cart context — legacy capture paths still work without
 * an upload while G.5 deprecates them.
 */
async function uploadAttachedDoc(
  input: RouteCaptureInput,
  attachedDoc: AttachedDoc,
  extras?: { azureDiRawType?: string; azureDiConfidence?: number; azureDiExtractionJson?: string },
): Promise<{ ok: boolean; error?: string; spItemId?: number }> {
  const { cartId, personRefId, officerCode, apiClient } = input;
  if (!cartId || !personRefId || !officerCode) return { ok: true };

  // Compute a best-effort client-side digest. Server recomputes + trusts its
  // own hash; this is purely a corruption-detection signal. Failure here is
  // never fatal — we send the request without sha256 if subtle.crypto is
  // unavailable.
  const sha256 = await sha256OfBase64(input.base64);

  const body: UploadAttachedRequest = {
    cartId,
    personRefId,
    docCode: attachedDoc.docCode,
    filename: attachedDoc.filename,
    contentType: attachedDoc.contentType,
    sizeBytes: attachedDoc.sizeBytes,
    sourceType: input.sourceType ?? 'cip-scan',
    base64: input.base64,
    officerCode,
    capturedAt: attachedDoc.capturedAt,
    ...(sha256 ? { sha256 } : {}),
    ...(input.entityType ? { entityType: input.entityType } : {}),
    ...(input.sessionId ? { sessionId: input.sessionId } : {}),
    ...(input.person.cifId ? { cifNumber: input.person.cifId } : {}),
    ...(extras?.azureDiRawType ? { azureDIRawType: extras.azureDiRawType } : {}),
    ...(extras?.azureDiConfidence !== undefined ? { azureDIConfidence: extras.azureDiConfidence } : {}),
    ...(extras?.azureDiExtractionJson ? { azureDIExtractionJson: extras.azureDiExtractionJson } : {}),
  };

  try {
    const resp = await apiClient.retail.documents.uploadAttached(body);
    // Server returns the bare response shape (not enveloped) per H.1c contract.
    if (resp && typeof resp === 'object' && 'fileUrl' in resp && resp.fileUrl) {
      attachedDoc.fileUrl = resp.fileUrl;
      attachedDoc.spItemId = resp.spItemId;
      attachedDoc.sha256 = resp.sha256;
      if (extras?.azureDiRawType) attachedDoc.azureDiRawType = extras.azureDiRawType;
      if (extras?.azureDiConfidence !== undefined) attachedDoc.azureDiConfidence = extras.azureDiConfidence;
      return { ok: true, spItemId: resp.spItemId };
    }
    attachedDoc.fileUrl = '';
    return { ok: false, error: 'Upload returned no fileUrl' };
  } catch (err) {
    attachedDoc.fileUrl = '';
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, error: msg };
  }
}

/**
 * Internal — runs the classification + extraction pipeline and produces the
 * baseline RouteCaptureResult (without the SP upload step). The public
 * `routeCapture` wrapper layers the upload on top.
 */
async function routeCaptureInner(
  input: RouteCaptureInput,
): Promise<{
  result: RouteCaptureResult;
  uploadExtras?: { azureDiRawType?: string; azureDiConfidence?: number };
}> {
  const { docType: docTypeValue, base64, mimeType, person, apiClient, cartId } = input;
  const docType = findDocType(docTypeValue);
  if (!docType) {
    // Fallback to OTHER — we should always be able to attach.
    const fallback = findDocType('other')!;
    const attachedDoc = await makeAttachedDoc(fallback, base64, mimeType);
    return {
      result: {
        patch: {},
        attachedDoc,
        extractedFieldKeys: [],
        message: `Captured · routed to ${fallback.slot} slot (unknown doc type)`,
      },
    };
  }

  const attachedDoc = await makeAttachedDoc(docType, base64, mimeType);

  // No-extraction path — just attach.
  if (docType.mode === 'no-extraction') {
    return {
      result: {
        patch: {},
        attachedDoc,
        extractedFieldKeys: [],
        message: `Captured · routed to ${docType.slot} slot · no extraction needed`,
      },
    };
  }

  // Photo-ID extraction — DL / State ID / US Passport.
  // (Extracts via Azure Content Understanding; see IdExtractionService.)
  if (docType.mode === 'azure-di-id') {
    try {
      const svc = new IdExtractionService(apiClient);
      // Cart linkage (Bill 2026-10-16): the dbo.DocumentExtract row is joinable to the
      // cart only when the cart id rides along; the officer code is stamped server-side.
      const ex = await svc.extractFromImage(base64, mimeType, undefined, {
        ...(cartId ? { cartId } : {}),
        ...(input.personRefId ? { personRefId: input.personRefId } : {}),
        expectedDocType: docType.value,
      });
      // 0600 — canonical extracts key. The generic slots (primary-id /
      // secondary-id) store the extract under the CLASSIFIED canonical doc
      // type ('us-drivers-license', 'us-passport', …) so readers that resolve
      // by classified value (CIP slot-machine cipExtractsFor) find it. Falls
      // back to the route value when DI couldn't classify the subtype.
      const detectedForKey = azureDiTypeToDocTypeValue(ex.rawDocumentType);
      const extractsKey = detectedForKey !== 'other' ? detectedForKey : docType.value;
      const { patch, keys } = buildPatchFromAzureDiId(person, ex, extractsKey);
      const uploadExtras: { azureDiRawType?: string; azureDiConfidence?: number } = {};
      if (ex.rawDocumentType) uploadExtras.azureDiRawType = ex.rawDocumentType;
      // ExtractedIdFields.confidence is a per-field map; derive a "headline"
      // value as the average across the populated entries. This is purely
      // informational on the audit trail — backend treats it as opaque.
      if (ex.confidence && typeof ex.confidence === 'object') {
        const vals = Object.values(ex.confidence).filter(
          (v): v is number => typeof v === 'number' && Number.isFinite(v),
        );
        if (vals.length > 0) {
          uploadExtras.azureDiConfidence =
            vals.reduce((a, b) => a + b, 0) / vals.length;
        }
      }
      // v0.14.20 — mis-routing detection. Compare officer-selected docType
      // value against the docType Azure DI returned (normalized through
      // azureDiTypeToDocTypeValue). Warning surfaces in the result so the
      // dialog can show a confirm-or-rescan banner before the patch lands.
      const mismatchWarning = detectMisrouting(docType.value, detectedForKey);
      return {
        result: {
          patch,
          attachedDoc,
          extractedFieldKeys: keys,
          message: `Routed to ${docType.slot} slot · ${keys.length} field${keys.length === 1 ? '' : 's'} auto-filled`,
          ...(ex.rawDocumentType ? { detectedRawType: ex.rawDocumentType } : {}),
          ...(ex.uprightRotationDeg ? { uprightRotationDeg: ex.uprightRotationDeg } : {}),
          ...(mismatchWarning ? { mismatchWarning } : {}),
        },
        uploadExtras,
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        result: {
          patch: {},
          attachedDoc,
          extractedFieldKeys: [],
          message: `Captured · extraction failed — re-scan or fill fields manually`,
          extractionError: msg,
        },
      };
    }
  }

  // /v3/nra/scan path.
  if (docType.mode === 'v3-nra' && docType.nraType) {
    try {
      const resp = await apiClient.retail.nra.scan({
        documentType: docType.nraType,
        image: base64,
        mimeType,
        ...(person.cifId ? { cifId: person.cifId } : {}),
        ...(cartId ? { cartId } : {}),
      });
      if (!resp.success || !resp.fields) {
        return {
          result: {
            patch: {},
            attachedDoc,
            extractedFieldKeys: [],
            message: `Captured · extraction failed — re-scan or fill fields manually`,
            ...(resp.error ? { extractionError: resp.error } : {}),
          },
        };
      }
      // Build the patch regardless of confidence — the empty-fields-only
      // guard in buildPatchFromNra ensures officer-typed values are never
      // clobbered. low-confidence fields are surfaced via the message +
      // mismatchWarning so the FSR can re-scan if needed.
      //
      // Bill 2026-05-28: prior behavior dropped the entire patch when ANY
      // field was below minConf — produced the "No fields extracted" green
      // card per-tile bug (the prebuilt-idDocument MRZ confidence is
      // routinely under threshold on dark/low-light green card photos).
      const { patch, keys, conflicts } = buildPatchFromNra(person, resp.fields, docType.nraType, docType.value);
      // BACKLOG-0762 — I-20 captures resolve the extracted SchoolCode against
      // the SEVIS catalog (campusName display + CD F141 `f141Employer` +
      // F/M-certification + decertified advisories). Non-fatal by design:
      // lookup failure keeps the extract-based values and adds a note.
      let schoolWarning: string | undefined;
      let schoolNote = '';
      if (docType.nraType === 'i20') {
        const lookup = await applyI20SchoolCatalogLookup(apiClient, person, resp.fields, patch, keys);
        if (lookup.warnings.length > 0) schoolWarning = lookup.warnings.join(' ');
        if (lookup.note) schoolNote = ` · ${lookup.note}`;
      }
      const lowConfNote =
        resp.needsConfirm.length > 0
          ? ` · ${resp.needsConfirm.length} field${resp.needsConfirm.length === 1 ? '' : 's'} need${resp.needsConfirm.length === 1 ? 's' : ''} confirmation`
          : '';
      return {
        result: {
          patch,
          attachedDoc,
          extractedFieldKeys: keys,
          message: `Routed to ${docType.slot} slot · ${keys.length} field${keys.length === 1 ? '' : 's'} auto-filled${lowConfNote}${schoolNote}`,
          ...(conflicts.length > 0 ? { conflicts } : {}),
          ...(schoolWarning ? { mismatchWarning: schoolWarning } : {}),
          ...(resp.needsConfirm.length > 0 ? { needsConfirm: resp.needsConfirm } : {}),
        },
      };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        result: {
          patch: {},
          attachedDoc,
          extractedFieldKeys: [],
          message: `Captured · extraction failed — re-scan or fill fields manually`,
          extractionError: msg,
        },
      };
    }
  }

  // Should never hit — exhaustive switch above.
  return {
    result: {
      patch: {},
      attachedDoc,
      extractedFieldKeys: [],
      message: `Captured · routed to ${docType.slot} slot`,
    },
  };
}

/**
 * Centralized routing entry point — the UnifiedCaptureSurface calls this
 * after the officer accepts a doc-type classification.
 *
 * Phase H.1c (2026-05-01): after the classification + extraction steps
 * resolve, the AttachedDoc is uploaded to ao_Documents via
 * `/v3/documents/upload-attached`. On success the response stamps `fileUrl`,
 * `spItemId`, `sha256` onto the AttachedDoc before it's returned. On
 * failure the AttachedDoc is still returned (with `fileUrl=''`) so the
 * dialog can keep its in-memory copy + retry the upload.
 *
 * The upload is SKIPPED when the caller didn't thread `cartId`,
 * `personRefId`, and `officerCode` — legacy capture paths that haven't
 * been migrated still get the AttachedDoc back without a server-of-truth
 * pointer. G.5 closes that gap by retiring the per-tab capture surfaces.
 */
export async function routeCapture(input: RouteCaptureInput): Promise<RouteCaptureResult> {
  const { result, uploadExtras } = await routeCaptureInner(input);

  // Upload step — only when the caller threaded cart context AND the
  // extraction didn't completely fail. (For the no-extraction "other"
  // bucket we still upload — the officer chose to attach a file.)
  const canUpload = !!input.cartId && !!input.personRefId && !!input.officerCode;
  if (!canUpload) return result;

  const upload = await uploadAttachedDoc(input, result.attachedDoc, uploadExtras);
  if (upload.ok) {
    return {
      ...result,
      ...(upload.spItemId !== undefined ? { spItemId: upload.spItemId } : {}),
    };
  }
  return {
    ...result,
    ...(upload.error ? { uploadError: upload.error } : {}),
  };
}
